import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Ban,
  CheckCircle2,
  CircleDot,
  ClipboardCheck,
  Clock,
  FileText,
  IndianRupee,
  MessageSquare,
  PackageCheck,
  RotateCw,
  ScanLine,
  Send,
  Truck,
  Undo2,
  Users,
  XCircle,
  XOctagon,
} from "lucide-react";
import { D2C_OPS_ORDERS_API_URL } from "../../Shared/constant";
import { Badge, toneOf } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { cn } from "./ui/utils";
import { OfferContact, StaffMember } from "./DealerContacts";
import StageProgress, { StageSteps } from "./StageProgress";
import StateBlock from "./StateBlock";
import useD2cGet from "./useD2cGet";
import PdiPhotos from "./PdiPhotos";
import RepushDialog from "./RepushDialog";
import { hasD2cSwitchesAccess } from "./d2cAccess";
import { STAGE_LABELS } from "./stageSteps";
import {
  formatDateTime,
  formatKm,
  formatNumber,
  formatRupees,
  humaniseCode,
  stageTone,
} from "./d2cFormat";

const Field = ({ label, children, mono }) => (
  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
    <span className="tw-text-xs tw-text-muted-foreground">{label}</span>
    <span className={cn("tw-break-words tw-text-sm tw-text-foreground", mono && "tw-font-mono tw-text-xs")}>
      {children ?? "-"}
    </span>
  </div>
);

const Fields = ({ children }) => (
  <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 sm:tw-grid-cols-3">{children}</div>
);

const Section = ({ title, children, aside }) => (
  <Card>
    <CardHeader className="tw-items-center tw-pb-2">
      <CardTitle as="h4">{title}</CardTitle>
      {aside}
    </CardHeader>
    <CardContent className="tw-space-y-3">{children}</CardContent>
  </Card>
);

const Muted = ({ children }) => <div className="tw-text-sm tw-text-muted-foreground">{children}</div>;

const RoutingSteps = ({ routing }) => {
  const steps = [
    { label: "In radius", value: routing.dealers_in_radius },
    { label: "Accepting", value: routing.dealers_accepting },
    { label: "GST ok", value: routing.dealers_gst_ok },
    { label: "With stock", value: routing.dealers_with_stock },
    { label: "Offered", value: routing.dealers_offered },
  ];
  return (
    <ol className="tw-m-0 tw-grid tw-list-none tw-grid-cols-5 tw-gap-1.5 tw-p-0">
      {steps.map((step) => {
        const zero = !(Number(step.value) > 0);
        return (
          <li
            key={step.label}
            className={cn(
              "tw-flex tw-flex-col tw-items-center tw-rounded-md tw-border tw-px-1 tw-py-2 tw-text-center",
              zero ? "tw-border-red-200 tw-bg-red-50 tw-text-red-700" : "tw-border-blue-100 tw-bg-blue-50 tw-text-blue-800",
            )}
          >
            <strong className="tw-text-lg tw-font-semibold tw-leading-6">{formatNumber(step.value)}</strong>
            <span className="tw-text-[11px] tw-leading-4">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
};

// Fulfilment milestones from the fulfilment row (separate from the activity log).
// The second step is where the order went: offered to dealers (time from the
// timeline's OFFERED entry, which exists even for orders older than the routing
// log) or sent straight to central.
export const routingMilestone = (routing, timeline) => {
  if (routing?.outcome === "HANDED_BACK") {
    return { label: "Sent to central", at: routing.decided_at };
  }
  const offered = (timeline || []).find((event) => event.kind === "OFFERED");
  return { label: "Sent to dealers", at: offered?.at || routing?.decided_at };
};

// A missing time reads "Not recorded" once a later step has happened (the
// order moved past it), and "Not yet" while the order has not got there.
export const missingLabel = (events, index) =>
  events.slice(index + 1).some((event) => event.at) ? "Not recorded" : "Not yet";

const Milestones = ({ order, routing, fulfilment, timeline }) => {
  const events = [
    { label: "Order placed", at: order?.created_at },
    routingMilestone(routing, timeline),
    { label: "Accepted by dealer", at: fulfilment?.accepted_at },
    { label: "Frame scanned", at: fulfilment?.scanned_at },
    { label: "PDI done", at: fulfilment?.pdi_done_at },
    { label: "OTP sent", at: fulfilment?.otp_sent_at },
    { label: "Delivered", at: fulfilment?.delivered_at },
  ];
  if (fulfilment?.cancelled_at) events.push({ label: "Cancelled", at: fulfilment.cancelled_at, bad: true });
  return (
    <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-1 tw-p-0 sm:tw-grid-cols-2">
      {events.map((event, index) => (
        <li key={event.label} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-md tw-px-2 tw-py-1 tw-text-sm">
          <span className="tw-flex tw-items-center tw-gap-2">
            <span
              aria-hidden="true"
              className={cn(
                "tw-size-2 tw-rounded-full",
                event.at ? (event.bad ? "tw-bg-red-500" : "tw-bg-primary") : "tw-bg-slate-300",
              )}
            />
            <span className={event.at ? "tw-text-foreground" : "tw-text-muted-foreground"}>{event.label}</span>
          </span>
          <span className="tw-text-xs tw-text-muted-foreground">{event.at ? formatDateTime(event.at) : missingLabel(events, index)}</span>
        </li>
      ))}
    </ul>
  );
};

// Icon and colour per timeline kind. ERP steps and SMS turn red when they failed.
const KIND_STYLE = {
  HANDED_BACK: { icon: Undo2, tone: "amber" },
  OFFERED: { icon: Send, tone: "blue" },
  ACCEPTED: { icon: CheckCircle2, tone: "green" },
  DECLINED: { icon: XCircle, tone: "red" },
  TAKEN_BY_OTHER: { icon: Users, tone: "grey" },
  NO_RESPONSE: { icon: Clock, tone: "amber" },
  RELEASED: { icon: Undo2, tone: "amber" },
  ACCEPT_REFUSED: { icon: Ban, tone: "red" },
  FRAME_SCANNED: { icon: ScanLine, tone: "blue" },
  PDI_DONE: { icon: ClipboardCheck, tone: "blue" },
  OUT_FOR_DELIVERY: { icon: Truck, tone: "blue" },
  DELIVERED: { icon: PackageCheck, tone: "green" },
  CANCELLED: { icon: XOctagon, tone: "red" },
  OTP_SMS: { icon: MessageSquare, tone: "grey" },
  ERP_STEP: { icon: FileText, tone: "blue" },
  FEE: { icon: IndianRupee, tone: "grey" },
  REPUSHED: { icon: RotateCw, tone: "blue" },
  // The dealer's replacement cycle, ordered once the ERP close-out finishes.
  REPLENISHMENT_DISPATCHED: { icon: Truck, tone: "blue" },
  REPLENISHMENT_DELIVERED: { icon: PackageCheck, tone: "green" },
  REPLENISHMENT_FAILED: { icon: XOctagon, tone: "red" },
};
const DOT = {
  blue: "tw-bg-blue-50 tw-text-blue-700 tw-ring-blue-200",
  green: "tw-bg-emerald-50 tw-text-emerald-700 tw-ring-emerald-200",
  amber: "tw-bg-amber-50 tw-text-amber-700 tw-ring-amber-200",
  red: "tw-bg-red-50 tw-text-red-700 tw-ring-red-200",
  grey: "tw-bg-slate-100 tw-text-slate-600 tw-ring-slate-200",
};
const FAILED = new Set(["FAILED", "ERROR"]);
const OK = new Set(["DONE", "SUCCESS", "COMPLETE", "SENT", "DELIVERED"]);

export const kindStyle = (event) => {
  const base = KIND_STYLE[event?.kind] || { icon: CircleDot, tone: "grey" };
  const status = String(event?.status || "").toUpperCase();
  if ((event?.kind === "ERP_STEP" || event?.kind === "OTP_SMS") && FAILED.has(status)) return { ...base, tone: "red" };
  if (event?.kind === "ERP_STEP" && OK.has(status)) return { icon: CheckCircle2, tone: "green" };
  return base;
};

// Extra facts an activity event may carry, in muted text under its label.
const activityDetails = (event) =>
  [
    event.store && [event.store.franchise_code, event.store.name].filter(Boolean).join(" · "),
    event.by?.name && `by ${event.by.name}`,
    event.reason && humaniseCode(event.reason),
    event.status && humaniseCode(event.status),
    Number(event.attempts) > 1 && `${event.attempts} attempts`,
    event.detail,
  ]
    .filter(Boolean)
    .join(" · ");

// The order's recorded activity from the server's `timeline`, oldest first.
export const ActivityTimeline = ({ events }) => {
  if (!events?.length) return <Muted>No activity recorded yet.</Muted>;
  return (
    <ol aria-label="Order activity" className="tw-relative tw-m-0 tw-list-none tw-p-0">
      {events.map((event, index) => {
        const details = activityDetails(event);
        const { icon: Icon, tone } = kindStyle(event);
        const last = index === events.length - 1;
        return (
          <li key={`${event.at}-${event.kind}-${index}`} data-kind={event.kind} data-tone={tone} className="tw-relative tw-flex tw-gap-3 tw-pb-4 last:tw-pb-0">
            {!last && <span aria-hidden="true" className="tw-absolute tw-bottom-0 tw-left-[13px] tw-top-7 tw-w-px tw-bg-border" />}
            <span className={cn("tw-relative tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-ring-1", DOT[tone])}>
              <Icon className="tw-size-3.5" aria-hidden="true" />
            </span>
            <div className="tw-min-w-0 tw-flex-1 tw-pt-0.5">
              <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-x-3">
                <span className="tw-text-sm tw-font-medium tw-text-foreground">{event.label || humaniseCode(event.kind)}</span>
                <time className="tw-text-xs tw-text-muted-foreground">{formatDateTime(event.at)}</time>
              </div>
              {details && <div className="tw-mt-0.5 tw-break-words tw-text-xs tw-text-muted-foreground">{details}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const DOCUMENTS = [
  ["sales_return", "Sales return"],
  ["credit_note", "Credit note"],
  ["margin_credit_note", "Margin credit note"],
  ["sales_order", "Sales order"],
  ["delivery_note", "Delivery note"],
  ["invoice", "Invoice"],
];

// Offer rows the OMS can reopen for the same dealer (REOPENABLE in dealerapp/d2c/ops/repush.py).
const REPUSHABLE = new Set(["DECLINED", "RELEASED", "EXPIRED", "TAKEN"]);

const SmallTable = ({ head, children }) => (
  <div className="tw-overflow-hidden tw-rounded-md tw-border">
    <Table className="tw-text-xs">
      <TableHeader>
        <TableRow>{head}</TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  </div>
);

export const OrderDetail = ({ detail, onRepush }) => {
  const { order = {}, routing, fulfilment, offers = [], otp, erp, fee, timeline, photos = [] } = detail;
  return (
    <>
      <Section title="Order">
        <Fields>
          <Field label="Status">{humaniseCode(order.status)}</Field>
          <Field label="Route">{humaniseCode(order.route)}</Field>
          <Field label="Product">{order.product}</Field>
          <Field label="Created">{formatDateTime(order.created_at)}</Field>
          <Field label="Pincode">{order.pincode}</Field>
          <Field label="City">{order.city}</Field>
        </Fields>
      </Section>

      <Section title="Activity">
        <ActivityTimeline events={timeline} />
      </Section>

      <Section title="Routing decision">
        {routing ? (
          <>
            <Fields>
              <Field label="Decided">{formatDateTime(routing.decided_at)}</Field>
              <Field label="Outcome">{humaniseCode(routing.outcome)}</Field>
              <Field label="Reason">{humaniseCode(routing.reason)}</Field>
              <Field label="Nearest offered">{formatKm(routing.nearest_km)}</Field>
              <Field label="Fee shown">{formatRupees(routing.fee_amount)}</Field>
            </Fields>
            <RoutingSteps routing={routing} />
          </>
        ) : (
          <Muted>No routing decision recorded for this order.</Muted>
        )}
      </Section>

      <Section
        title={`Offers (${offers.length})`}
        aside={
          onRepush && (
            <Button variant="outline" size="sm" onClick={() => onRepush(null)}>
              <RotateCw className="tw-size-3.5" aria-hidden="true" />
              Re-push to a dealer
            </Button>
          )
        }
      >
        {offers.length === 0 ? (
          <Muted>No dealer was offered this order.</Muted>
        ) : (
          <SmallTable
            head={
              <>
                <TableHead>Dealer</TableHead>
                <TableHead>What they did</TableHead>
                <TableHead className="tw-text-right">Distance</TableHead>
                <TableHead className="tw-text-right">Fee shown</TableHead>
                <TableHead>Responded</TableHead>
                <TableHead>Reason</TableHead>
              </>
            }
          >
            {offers.map((offer) => (
              <TableRow key={offer.store_id || offer.franchise_code}>
                <TableCell className="tw-min-w-[200px] tw-whitespace-normal tw-align-top">
                  <div className="tw-font-medium">{offer.name || "-"}</div>
                  <div className="tw-font-mono tw-text-[11px] tw-text-muted-foreground">{offer.franchise_code}</div>
                  <OfferContact contact={offer.contact} rm={offer.rm} />
                </TableCell>
                <TableCell className="tw-whitespace-normal tw-align-top">
                  {humaniseCode(offer.status)}
                  {onRepush && offer.store_id && REPUSHABLE.has(offer.status) && (
                    <Button
                      variant="link"
                      size="sm"
                      className="tw-ml-2 tw-h-auto tw-p-0 tw-text-xs"
                      aria-label={`Re-push to ${offer.franchise_code || offer.name || "this dealer"}`}
                      onClick={() => onRepush(offer.store_id)}
                    >
                      Re-push
                    </Button>
                  )}
                  {offer.responded_by && (
                    <div className="tw-mt-0.5 tw-text-[11px] tw-leading-4 tw-text-muted-foreground">
                      <StaffMember person={offer.responded_by} prefix="by " />
                    </div>
                  )}
                </TableCell>
                <TableCell className="tw-text-right tw-tabular-nums">
                  {formatKm(offer.distance_km)}
                  {offer.distance_band && <div className="tw-text-[11px] tw-text-muted-foreground">{humaniseCode(offer.distance_band)}</div>}
                </TableCell>
                <TableCell className="tw-text-right tw-tabular-nums">{formatRupees(offer.fee_shown)}</TableCell>
                <TableCell>{formatDateTime(offer.responded_at)}</TableCell>
                <TableCell>{humaniseCode(offer.reason)}</TableCell>
              </TableRow>
            ))}
          </SmallTable>
        )}
      </Section>

      <Section title="Fulfilment">
        <Fields>
          <Field label="State">{humaniseCode(fulfilment?.state)}</Field>
          <Field label="Exit reason">{humaniseCode(fulfilment?.exit_reason)}</Field>
          <Field label="Frame number" mono>
            {fulfilment?.frame_number}
          </Field>
          <Field label="Reschedules">{formatNumber(fulfilment?.reschedule_count)}</Field>
          <Field label="SLA breach">{fulfilment?.breach_flag ? <Badge tone="danger">Breached</Badge> : "No"}</Field>
          {fulfilment?.accepted_by && (
            <Field label="Accepted by">
              <StaffMember person={fulfilment.accepted_by} />
            </Field>
          )}
          {fulfilment?.handled_by && (
            <Field label="Handled by">
              <StaffMember person={fulfilment.handled_by} />
            </Field>
          )}
        </Fields>
        <Milestones order={order} routing={routing} fulfilment={fulfilment} timeline={timeline} />
      </Section>

      <Section title="PDI photos">
        <PdiPhotos photos={photos} />
      </Section>

      <Section title="Delivery OTP and SMS">
        <Fields>
          <Field label="Codes issued">{formatNumber(otp?.codes_issued)}</Field>
          <Field label="Resends used">{formatNumber(otp?.resends_used)}</Field>
          <Field label="Attempts used">{formatNumber(otp?.attempts_used)}</Field>
        </Fields>
        {otp?.sms?.length > 0 ? (
          <SmallTable
            head={
              <>
                <TableHead>SMS status</TableHead>
                <TableHead className="tw-text-right">Attempts</TableHead>
                <TableHead>Created</TableHead>
              </>
            }
          >
            {otp.sms.map((sms, index) => (
              <TableRow key={`${sms.created_at}-${index}`} className={sms.status === "FAILED" ? "tw-text-red-700" : ""}>
                <TableCell>{humaniseCode(sms.status)}</TableCell>
                <TableCell className="tw-text-right tw-tabular-nums">{formatNumber(sms.attempts)}</TableCell>
                <TableCell>{formatDateTime(sms.created_at)}</TableCell>
              </TableRow>
            ))}
          </SmallTable>
        ) : (
          <Muted>No SMS sent for this order.</Muted>
        )}
      </Section>

      <Section title="ERP close-out">
        {erp?.steps?.length > 0 ? (
          <SmallTable
            head={
              <>
                <TableHead>Step</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="tw-text-right">Attempts</TableHead>
                <TableHead>Updated</TableHead>
              </>
            }
          >
            {erp.steps.map((step, index) => (
              <React.Fragment key={`${step.kind}-${index}`}>
                <TableRow>
                  <TableCell>{step.label || humaniseCode(step.kind)}</TableCell>
                  <TableCell>
                    <Badge tone={step.status === "FAILED" ? "danger" : step.status === "DONE" ? "success" : "neutral"}>
                      {humaniseCode(step.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="tw-text-right tw-tabular-nums">{formatNumber(step.attempts)}</TableCell>
                  <TableCell>{formatDateTime(step.updated_at)}</TableCell>
                </TableRow>
                {step.last_error && (
                  <TableRow>
                    <TableCell colSpan={4} className="tw-whitespace-normal">
                      <pre className="tw-m-0 tw-max-h-40 tw-overflow-y-auto tw-whitespace-pre-wrap tw-break-words tw-rounded-md tw-bg-red-50 tw-px-2 tw-py-1.5 tw-font-mono tw-text-xs tw-text-red-700">
                        {step.last_error}
                      </pre>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </SmallTable>
        ) : (
          <Muted>No ERP steps yet.</Muted>
        )}
        <Fields>
          {DOCUMENTS.map(([key, label]) => (
            <Field key={key} label={label} mono>
              {erp?.documents?.[key]}
            </Field>
          ))}
        </Fields>
      </Section>

      <Section title="Dealer fee">
        <Fields>
          <Field label="Status">{humaniseCode(fee?.status)}</Field>
          <Field label="Amount">{formatRupees(fee?.amount)}</Field>
          <Field label="Credit note" mono>
            {fee?.cn_number}
          </Field>
        </Fields>
      </Section>
    </>
  );
};

const OrderDetailDrawer = ({ orderId, onClose }) => {
  const { selfInfo } = useSelector((state) => state.adminReducers);
  const url = orderId ? `${D2C_OPS_ORDERS_API_URL}/${encodeURIComponent(orderId)}` : null;
  const { data, loading, error, reload } = useD2cGet(url);
  // null = closed; otherwise the store to preselect ("" for none).
  const [repushStore, setRepushStore] = useState(null);
  const canRepush = hasD2cSwitchesAccess(selfInfo);
  const stage = data?.stage;

  return (
    <Sheet open={Boolean(orderId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent>
        <SheetHeader>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <SheetTitle className="tw-font-mono">{data?.order?.order_code || "D2C order"}</SheetTitle>
            {stage && <Badge tone={toneOf(stageTone(stage))}>{STAGE_LABELS[stage] || humaniseCode(stage)}</Badge>}
          </div>
          <SheetDescription>
            {[data?.order?.product, data?.order?.city].filter(Boolean).join(" · ") || "Order details from the OMS."}
          </SheetDescription>
          {stage && (
            <div>
              <StageProgress stage={stage} wide className="tw-w-full" />
              <StageSteps stage={stage} />
            </div>
          )}
        </SheetHeader>
        <SheetBody>
          <StateBlock loading={loading} error={error} onRetry={reload} isEmpty={!data} emptyText="No details for this order.">
            {data && <OrderDetail detail={data} onRepush={canRepush ? (storeId) => setRepushStore(storeId || "") : undefined} />}
          </StateBlock>
        </SheetBody>
      </SheetContent>
      {canRepush && (
        <RepushDialog
          orderId={orderId}
          open={repushStore !== null}
          preselectStoreId={repushStore || null}
          onClose={() => setRepushStore(null)}
          onDone={() => {
            setRepushStore(null);
            reload();
          }}
          onRefused={reload}
        />
      )}
    </Sheet>
  );
};

export default OrderDetailDrawer;
